import { init as initApm } from "@elastic/apm-rum";
import { API_ROUTE, APM_ENVIRONMENT, APM_SERVER_URL, FAST_API_URL, REPORT_URL } from "./config/constants";
import { isMutedError } from "./config/errorNoise";
import { makeRef, setApmActive } from "./config/errorRef";

const safeOrigin = url => {
    try {
        return new URL(url).origin;
    } catch {
        return null;
    }
};

const distributedTracingOrigins = Array.from(new Set([API_ROUTE, FAST_API_URL, REPORT_URL].map(safeOrigin).filter(Boolean)));

// Enable RUM only when we have an APM server URL configured
const active = Boolean(APM_SERVER_URL);

export const apm = initApm({
    serviceName: "fca-frontend",
    serviceVersion: (window.__ENV && window.__ENV.BUILD_VERSION) || "unknown",
    serverUrl: APM_SERVER_URL,
    environment: APM_ENVIRONMENT,
    active,

    // This is the key to end-to-end distributed tracing: RUM will add `traceparent`
    // (and optionally `tracestate`) to XHR/fetch requests to these origins.
    distributedTracingOrigins,
    propagateTracestate: true
});

// Let the alert layer know whether showing a reference is meaningful, without it having to
// depend on this module (and therefore on runtime config) to render a message.
setApmActive(active);

const currentTraceId = () => {
    try {
        const tx = typeof apm?.getCurrentTransaction === "function" ? apm.getCurrentTransaction() : null;
        return tx?.traceId || tx?.trace_id || null;
    } catch {
        return null;
    }
};

/**
 * Reports an error to Elasticsearch and returns the reference support can search for.
 *
 * `apm.captureError(error)` takes ONLY the error — a second options argument is silently
 * discarded — so context has to travel on the error itself: own properties become
 * `error.custom`, and the message is full-text searchable. Never throws.
 */
export const reportError = (error, context = {}) => {
    const ref = context.ref || makeRef();
    try {
        if (active && apm) {
            const original = error instanceof Error ? error : new Error(String(error));

            // A copy, so prefixing the reference doesn't mutate the caller's error.
            const reportable = new Error(`[ref:${ref}] ${original.message}`);
            reportable.name = original.name;
            reportable.stack = original.stack;
            Object.assign(reportable, { ...context, ref, traceId: currentTraceId() });

            apm.captureError(reportable);
        }
    } catch (_) {
        /* a failure to report must never surface to the user */
    }
    return ref;
};

// --- ECS JSON browser console logs with APM correlation (mandatory) ---
// Note: browser logs are still "console logs"; if you ship browser logs somewhere,
// this ensures they're already in ECS shape and include trace context when available.
const _installEcsConsole = () => {
    const raw = {
        debug: console.debug?.bind(console),
        log: console.log?.bind(console),
        info: console.info?.bind(console),
        warn: console.warn?.bind(console),
        error: console.error?.bind(console)
    };

    const toMessage = args => {
        try {
            return args
                .map(a => {
                    if (typeof a === "string") return a;
                    try {
                        return JSON.stringify(a);
                    } catch {
                        return String(a);
                    }
                })
                .join(" ");
        } catch {
            return "<log_message_unavailable>";
        }
    };

    const getTraceContext = () => {
        try {
            const tx = typeof apm?.getCurrentTransaction === "function" ? apm.getCurrentTransaction() : null;
            return {
                traceId: tx?.traceId || tx?.trace_id || null,
                transactionId: tx?.id || tx?.transactionId || null
            };
        } catch {
            return { traceId: null, transactionId: null };
        }
    };

    const emit = (level, args) => {
        const env = window.__ENV || {};
        const { traceId, transactionId } = getTraceContext();

        const event = {
            "@timestamp": new Date().toISOString(),
            "log.level": level,
            message: toMessage(args),
            "service.name": "fca-frontend",
            "service.version": env.BUILD_VERSION || "unknown",
            "service.environment": env.ENVIRONMENT || "unknown",
            "trace.id": traceId || undefined,
            "transaction.id": transactionId || undefined,
            "url.full": window.location?.href
        };

        // Avoid undefined fields in JSON output
        Object.keys(event).forEach(k => event[k] === undefined && delete event[k]);

        // Print as ECS JSON
        (raw[level] || raw.log)(JSON.stringify(event));
    };

    console.debug = (...args) => emit("debug", args);
    console.log = (...args) => emit("info", args);
    console.info = (...args) => emit("info", args);
    console.warn = (...args) => emit("warning", args);
    console.error = (...args) => emit("error", args);
};

/* Note: do NOT add `window.addEventListener("error"/"unhandledrejection")` here. The RUM
   agent already registers both (error instrumentation is on by default), so uncaught errors
   and rejected promises reach Elasticsearch on their own — adding our own listeners would
   report every one of them twice. */

/**
 * Re-attach who the user is at startup.
 *
 * User context lives in memory and the app only sets it while signing in, but the auth cookie
 * outlives the JS context — so a refresh, a second tab, or a returning session reported errors
 * anonymously. No email: `crRequestorMail` holds the capital-request requestor's address, not
 * this user's.
 */
const restoreUserContext = () => {
    try {
        const id = localStorage.getItem("userId");
        if (!id) return; // signed out

        apm.setUserContext({
            id,
            username: localStorage.getItem("user") || localStorage.getItem("printed_name") || null
        });

        // Labels are indexed, so this is filterable in Kibana; user context largely isn't.
        const role = localStorage.getItem("role");
        if (role) {
            apm.addLabels({ user_role: role });
        }
    } catch (_) {
        /* storage throws in some privacy modes — never worth breaking startup */
    }
};

if (active) {
    restoreUserContext();
}

/* Drop already-diagnosed noise so it cannot bury real faults (see errorNoise.js). */
try {
    apm.addFilter(payload => {
        // Always return the payload: a falsy return drops the ENTIRE batch, transactions included.
        if (payload && Array.isArray(payload.errors)) {
            payload.errors = payload.errors.filter(err => !isMutedError(err?.exception?.message));
        }
        return payload;
    });
} catch (_) {
    /* filtering must never break startup */
}

const env = window?.__ENV?.ENVIRONMENT || process.env.NODE_ENV || "development";

const isDev = env === "development";

if (!isDev) {
    _installEcsConsole();
}

if (isDev) {
    console.log("Elastic APM RUM init", {
        active,
        APM_SERVER_URL,
        APM_ENVIRONMENT,
        distributedTracingOrigins
    });
}
